<script setup lang="ts" generic="T extends { uuid: string }">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"
import AsyncComboboxSearchList from "./AsyncComboboxSearchList.vue"
import { ComboboxPortalTargetKey } from "../types"

const portalTargetRef = inject(ComboboxPortalTargetKey, null)
const portalTarget = computed<string | HTMLElement>(
  () => portalTargetRef?.value ?? "body"
)

const props = withDefaults(
  defineProps<{
    modelValue: T[]
    items: T[]
    loading: boolean
    hasMore: boolean
    search: (query: string) => void
    labelFn: (item: T) => string
    placeholder?: string
    emptyLabel?: string
    disabled?: boolean
    creatable?: boolean
    createLabelFn?: (query: string) => string
  }>(),
  {
    placeholder: "Suchen...",
    emptyLabel: "Keine Treffer",
    disabled: false,
    creatable: false,
    createLabelFn: undefined
  }
)

const emit = defineEmits<{
  "update:modelValue": [T[]]
  "load-more": []
  "create": [string]
}>()

const open = ref(false)
const query = ref("")
const rootRef = ref<HTMLElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
const searchListRef = ref<{ focusInput: () => void } | null>(null)
const openerInputRef = ref<HTMLInputElement | null>(null)
const panelStyle = ref<Record<string, string>>({})

function positionPanel(): void {
  const root = rootRef.value
  if (!root) return
  const rect = root.getBoundingClientRect()
  const viewportH = window.innerHeight
  const spaceBelow = viewportH - rect.bottom - 8
  const spaceAbove = rect.top - 8
  const MIN_BELOW = 200

  if (spaceBelow >= MIN_BELOW || spaceBelow >= spaceAbove) {
    panelStyle.value = {
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      maxHeight: `${Math.max(spaceBelow, MIN_BELOW)}px`
    }
  } else {
    panelStyle.value = {
      bottom: `${viewportH - rect.top + 4}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      maxHeight: `${Math.max(spaceAbove, MIN_BELOW)}px`
    }
  }
}

function onViewportChange(): void {
  if (open.value) positionPanel()
}

onMounted(() => {
  window.addEventListener("scroll", onViewportChange, true)
  window.addEventListener("resize", onViewportChange)
})

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onViewportChange, true)
  window.removeEventListener("resize", onViewportChange)
})

watch(open, (isOpen) => {
  if (isOpen) nextTick(() => positionPanel())
})

const selectedIds = computed(() => new Set(props.modelValue.map((x) => x.uuid)))

function openDropdown(): void {
  if (props.disabled) return
  open.value = true
  query.value = ""
  props.search("")
  nextTick(() => searchListRef.value?.focusInput())
}

function focusOpener(): void {
  openerInputRef.value?.focus()
}

function onQueryUpdate(v: string): void {
  query.value = v
  props.search(v)
}

function onSelect(item: T): void {
  if (selectedIds.value.has(item.uuid)) return
  emit("update:modelValue", [...props.modelValue, item])
  query.value = ""
  props.search("")
}

function removeValue(item: T): void {
  emit("update:modelValue", props.modelValue.filter((x) => x.uuid !== item.uuid))
}

function onSubmitEmpty(): void {
  if (props.modelValue.length > 0) {
    emit("update:modelValue", props.modelValue.slice(0, -1))
  }
}

function onClose(): void {
  open.value = false
}

function onLoadMore(): void {
  emit("load-more")
}

// The dropdown stays open: the parent appends the created entity to modelValue once
// its mutation resolves, and the badge shows up like any other selection.
function onCreate(query: string): void {
  emit("create", query)
  props.search("")
}

function handleOutsideClick(e: MouseEvent): void {
  if (!open.value) return
  const target = e.target as Node | null
  if (target == null) return
  if (rootRef.value && rootRef.value.contains(target)) return
  if (panelRef.value && panelRef.value.contains(target)) return
  open.value = false
}

onMounted(() => { document.addEventListener("mousedown", handleOutsideClick) })
onBeforeUnmount(() => { document.removeEventListener("mousedown", handleOutsideClick) })
</script>

<template>
  <div ref="rootRef" class="relative">
    <div
      class="flex flex-wrap items-center gap-1 w-full min-h-10 border border-base-content/20 rounded-field bg-base-100 px-3 py-1.5 cursor-text focus-within:outline focus-within:outline-2 focus-within:outline-primary/50"
      :class="{ 'opacity-60': disabled }"
      @click="focusOpener"
    >
      <span
        v-for="item in modelValue"
        :key="item.uuid"
        :data-testid="`badge-${item.uuid}`"
        class="badge badge-primary gap-1 pr-1"
      >
        <slot name="badge" :item="item" :remove="() => removeValue(item)">
          <span>{{ labelFn(item) }}</span>
        </slot>
        <!--
          Idempotent double-fire: @mousedown.prevent + @click.stop fire both handlers in
          real browsers. removeValue is filter-based and idempotent, so the second call
          is a no-op. Tests using happy-dom's .trigger("click") only see one fire.
        -->
        <button
          v-if="!disabled"
          type="button"
          :data-testid="`badge-${item.uuid}-remove`"
          aria-label="Entfernen"
          class="ml-1 hover:bg-primary-content/20 rounded-full w-4 h-4 flex items-center justify-center"
          @mousedown.prevent="removeValue(item)"
          @click.stop="removeValue(item)"
        >×</button>
      </span>
      <span
        v-if="!open && modelValue.length === 0"
        class="flex-1 text-base-content/60 pointer-events-none"
      >{{ placeholder }}</span>
      <input
        ref="openerInputRef"
        data-testid="combobox-input"
        class="bg-transparent border-0 p-0 shadow-none outline-none focus:ring-0 focus:border-0 focus:outline-none"
        :class="open ? 'sr-only' : (modelValue.length > 0 ? 'flex-1 min-w-20' : 'sr-only')"
        :disabled="disabled"
        :placeholder="placeholder"
        :value="''"
        role="combobox"
        :aria-expanded="open"
        aria-autocomplete="list"
        @focus="openDropdown"
        @keydown="(e) => { if (e.key === 'Backspace' && modelValue.length > 0) onSubmitEmpty(); }"
      >
    </div>

  </div>

  <Teleport :to="portalTarget">
    <div
      v-if="open"
      ref="panelRef"
      class="fixed bg-base-100 border border-base-300 rounded-box shadow-lg z-50 flex flex-col overflow-hidden"
      :style="panelStyle"
    >
      <AsyncComboboxSearchList
        ref="searchListRef"
        :items="items"
        :loading="loading"
        :has-more="hasMore"
        :selected-ids="selectedIds"
        :query="query"
        :label-fn="labelFn"
        :placeholder="placeholder"
        :empty-label="emptyLabel"
        :creatable="creatable"
        :create-label-fn="createLabelFn"
        @update:query="onQueryUpdate"
        @select="onSelect"
        @create="onCreate"
        @load-more="onLoadMore"
        @close="onClose"
        @submit-empty="onSubmitEmpty"
      >
        <template v-if="$slots.option" #option="opt">
          <slot name="option" v-bind="opt" />
        </template>
      </AsyncComboboxSearchList>
    </div>
  </Teleport>
</template>
